import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import EnrollmentService from "../../../../services/enrollment_service";
import { useTenant } from "../../../../hooks/useTenant";
import ConfirmDialog from "../../../../components/Common/ConfirmDialog";

/**
 * useEnrollments
 * Hook para gerenciar matrículas de um membro no perfil.
 * Busca matrículas ativas e canceladas, e permite cancelar.
 */
const useEnrollments = (member) => {
    const { idTenant, user } = useTenant();
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [canceling, setCanceling] = useState(null); // ID da matrícula sendo cancelada

    const fetchEnrollments = useCallback(async () => {
        if (!idTenant || !member?.id) return;

        setLoading(true);
        try {
            const data = await EnrollmentService.listAllMemberEnrollments(idTenant, member.id);
            
            // Ordenar: ativas primeiro, depois por data de criação (mais recente)
            const sorted = data.sort((a, b) => {
                if (a.status === 'active' && b.status !== 'active') return -1;
                if (a.status !== 'active' && b.status === 'active') return 1;
                // Dentro do mesmo status, mais recente primeiro
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                return dateB - dateA;
            });

            setEnrollments(sorted);
        } catch (error) {
            console.error("Erro ao buscar matrículas:", error);
            toast.error("Erro ao carregar matrículas do aluno.");
        } finally {
            setLoading(false);
        }
    }, [idTenant, member?.id]);

    useEffect(() => {
        fetchEnrollments();
    }, [fetchEnrollments]);

    const handleCancelEnrollment = async (enrollment) => {
        const isConfirmed = await ConfirmDialog({
            title: `Cancelar matrícula?`,
            text: `Tem certeza que deseja cancelar a matrícula de "${member.fullName}" na turma? Esta ação irá remover o aluno das sessões futuras desta turma.`,
            confirmButtonText: 'Sim, cancelar matrícula!',
            confirmButtonColor: '#ff3d60',
        });

        if (!isConfirmed) return;

        setCanceling(enrollment.id);
        try {
            await EnrollmentService.cancelEnrollment(idTenant, user, enrollment);
            toast.success("Matrícula cancelada com sucesso.");
            // Recarregar a lista
            await fetchEnrollments();
        } catch (error) {
            console.error("Erro ao cancelar matrícula:", error);
            toast.error(error.message || "Erro ao cancelar matrícula.");
        } finally {
            setCanceling(null);
        }
    };

    return {
        enrollments,
        loading,
        canceling,
        handleCancelEnrollment,
        refresh: fetchEnrollments
    };
};

export default useEnrollments;
